import { create } from 'zustand';

import { TypewriterQueue } from '@/utils/TypewriterQueue';
import type { ChatFileDTO, SessionToolsResult } from '@/api/sessionApi';
import {
  feimaChatSessionConversationStop,
  feimaChatSessionGetTools,
  feimaChatSessionGetAnticipateQuestion,
} from '@/api/sessionApi';
import { feimaChatBotInfo } from '@/api/botApi';
import { fetchSourceApi } from '@/utils/fetch';
import { getStorageUser } from '@/utils/storage';
import {
  processConversationList,
  mergeDedup,
  type TimedTaskEditData,
} from '@/store/session';
import {
  TIMED_TASK_STATUS,
  getTimedTaskStatusText,
} from '@/pages/ai/timedTaskConfig';

// ── 分页大小常量（测试时可临时调小）──────────────────────────────────────────
/** 历史记录每页条数：初始加载、anchor 双向加载、上翻/下翻均使用此值 */
export const HISTORY_PAGE_SIZE = 20;
/** SSE 完成后拉取最新元数据时的条数 */
export const MERGED_LIST_FETCH_SIZE = 10;

// ── 公共类型（原定义在 useAgentSession.ts，迁入 store 以避免循环依赖）────────

export type BotItem = API.FeimaChatBotListResponse['data']['list'][0];
export type SessionItem = API.FeimaChatSessionListResponse['data']['list'][0];

export const AGENT_CHAT_PIN_EVENT = 'feima-agent-chat-pin';

export interface AgentChatPinEventDetail {
  botId: string;
  bot?: BotItem;
  timestamp: number;
}

export interface AgentMessage {
  id: number;
  _rawId?: number;
  sessionId: number;
  conversation: string;
  roleType: 'user' | 'assistant';
  nickname: string;
  avatar: string;
  createTime: number | string;
  isLoading?: boolean;
  thinkingContent?: string;
  isThinkingStreaming?: boolean;
  toolStatusList?: string[];
  isToolStatusStreaming?: boolean;
  memoryList?: any[];
  questionList?: string[];
  messageType?: string;
  skipMemoryNotification?: boolean;
  isError?: boolean;
  timedTaskData?: any;
  timedTaskResult?: any;
  receivedTaskData?: any;
  receivedTaskConfirmation?: any;
  conversationExtJsonDTO?: any;
  _isFresh?: boolean;
  /** SSE 已推送完毕，打字机队列仍在消化中；变为 true 后等 PrinterText 回调再把 isLoading 改为 false */
  _streamDone?: boolean;
  [key: string]: any;
}

export interface AgentSession {
  bot: BotItem | null;
  currentSession: SessionItem & { id: number };
  conversationList: SessionItem[];
  historyList: AgentMessage[];
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  isLoadingMore: boolean;
  isInitialHistoryLoading: boolean;
  isLoading: boolean;
  isBotUnavailable: boolean;
  qualification: any;
  sessionTools: SessionToolsResult | null;
  selectedModel: string;
  selectedMcpConfigId: number | null;
  thinkingEnabled: boolean;
  selectedFiles: ChatFileDTO[];
  selectedSkillIds: string[];
  videoGenerationEnabled: boolean;
  /** 输入框草稿文本，per-session 缓存，切换会话时保留，发送后清空 */
  draftText: string;
  pendingToolSelection: {
    model: string;
    mcpConfigId: number | null;
    thinkingEnabled: boolean;
    skillIds?: string[];
  } | null;
  sendMessage: (value: string, skillIds?: string[], targetBotId?: string) => Promise<void>;
  switchSession: (session: SessionItem) => Promise<void>;
  createNewSession: () => void;
  loadHistory: (
    anchorId?: number,
    onDone?: () => void,
    anchorItem?: unknown
  ) => Promise<void>;
  loadMoreOlder: (onDone?: (prevScrollHeight: number) => void) => Promise<void>;
  loadMoreNewer: (onDone?: () => void) => Promise<void>;
  abortAll: () => void;
  stopStreaming: () => Promise<void>;
  setSelectedModel: (
    model: string,
    options?: { preserveFiles?: boolean }
  ) => void;
  setSelectedMcpConfigId: (id: number | null) => void;
  setThinkingEnabled: (enabled: boolean) => void;
  setSelectedFiles: (
    files: ChatFileDTO[] | ((prev: ChatFileDTO[]) => ChatFileDTO[])
  ) => void;
  clearSelectedFiles: () => void;
  setSelectedSkillIds: (ids: string[]) => void;
  setVideoGenerationEnabled: (enabled: boolean) => void;
  /** 设置输入框草稿文本 */
  setDraftText: (text: string) => void;
  /**
   * note 会话专属：是否在发消息时附带笔记上下文。
   * 由 useAgentSession hook 管理，不进 store；普通会话此字段不存在。
   */
  noteContextEnabled?: boolean;
  setNoteContextEnabled?: (enabled: boolean) => void;
  setPendingToolSelection: (sel: AgentSession['pendingToolSelection']) => void;
  setHistoryList: (
    list: AgentMessage[] | ((prev: AgentMessage[]) => AgentMessage[])
  ) => void;
  addMessages: (messages: AgentMessage[]) => void;
  updateTimedTaskMessage: (taskId: number, data: TimedTaskEditData) => void;
  refreshConversationList: () => Promise<void>;
}

// ── 工具函数（从 useAgentSession 迁入）────────────────────────────────────────

export const makeSessionKey = (botId: string, noteId?: string) =>
  noteId ? `${botId}::${noteId}` : botId;

const getMergedItems = (res: any): any[] => res?.items ?? res?.list ?? [];

const getConversationId = (item: any) =>
  item?.type === 'MESSAGE' ? item.conversation?.id : item?.id;

const filterAnchorMessage = (items: any[], anchorId: number) =>
  items.filter((item: any) => getConversationId(item) !== anchorId);

const getAnchorMessageItem = (anchorItem: any, anchorId: number) => {
  const item =
    anchorItem?.type === 'MESSAGE' ? anchorItem.conversation : anchorItem;
  return item && getConversationId(item) === anchorId ? item : null;
};

const createWelcomeMessage = (
  currentBot: any,
  sessionId = 0
): AgentMessage => ({
  id: Math.random(),
  sessionId,
  conversation: currentBot?.prologue || '你好，有什么可以帮你的吗？',
  roleType: 'assistant',
  nickname: '智能体',
  avatar: currentBot?.avatar || '',
  createTime: Date.now(),
});

export const AGENT_CHAT_PIN_EVENT_FROM_STORE = 'feima-agent-chat-pin';

const notifyAgentChatPinned = (botId: string, bot?: BotItem | null) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(AGENT_CHAT_PIN_EVENT_FROM_STORE, {
      detail: { botId, bot: bot || undefined, timestamp: Date.now() },
    })
  );
};

// ── SessionAdapter ────────────────────────────────────────────────────────────

/**
 * 历史记录 fetcher 类型。
 * store 只注入分页参数，接口选择和响应归一化由 adapter 实现。
 */
export type HistoryFetcher = (params: {
  size: number;
  firstId?: number;
  lastId?: number;
}) => Promise<{ list: any[]; hasMore: boolean }>;

/**
 * 业务差异化适配器，由调用方（hook/组件）在 initSession 时传入。
 * store 只处理通用会话逻辑，所有业务特有行为由此接口注入。
 */
export interface SessionAdapter {
  /**
   * 历史记录 fetcher：封装接口选择 + 响应归一化。
   * store 只注入分页参数（size / firstId / lastId），其余由 adapter 处理。
   */
  historyFetcher: HistoryFetcher;
  /**
   * 确保 session 存在并返回 sessionId（0 表示失败）。
   * 首次发消息且 currentSession.id 为 0 时调用。
   * 同时负责把新 session 写入 store（通过传入的 patch 回调）。
   */
  ensureSession: (
    value: string,
    patch: (p: Partial<AgentSessionState>) => void
  ) => Promise<number>;
  /**
   * SSE 发送时注入的额外请求参数（每次 sendMessage 调用时实时取值）。
   * 普通会话返回 {}，note 会话返回 { noteContext: ... } 等。
   */
  getExtraSendParams?: () => Record<string, any>;
  /**
   * initSession 完成 bot 加载后，写入 store 的初始状态补丁。
   * 用于设置 session 类型特有的初始值（如 note 的 conversationList）。
   */
  getInitialPatch?: (bot: any) => Promise<Partial<AgentSessionState>>;
}

// ── Module-level refs Map（非序列化，不进 store）──────────────────────────────

interface SessionRefs {
  /** 每次 SSE 发送递增，用于检测跨 await 的 stale 状态 */
  streamIdRef: { current: number };
  /** 活跃的 AbortController 列表，AbortController 不可序列化 */
  controllersRef: { current: AbortController[] };
  /** loadHistory 竞态防护：后发起的请求 id 更大，旧请求结果被忽略 */
  historyLoadIdRef: { current: number };
  /** 当前会话的打字机队列实例，SSE 推来的字符经此按速率输出到 store */
  typewriterRef: { current: TypewriterQueue | null };
  /** 当前会话的思考内容打字机队列实例 */
  thinkingTypewriterRef: { current: TypewriterQueue | null };
  /** 业务差异化适配器，initSession 时由调用方注册 */
  adapterRef: { current: SessionAdapter | null };
}

export const sessionRefsMap = new Map<string, SessionRefs>();

export function getOrCreateRefs(key: string): SessionRefs {
  if (!sessionRefsMap.has(key)) {
    sessionRefsMap.set(key, {
      streamIdRef: { current: 0 },
      controllersRef: { current: [] },
      historyLoadIdRef: { current: 0 },
      typewriterRef: { current: null },
      thinkingTypewriterRef: { current: null },
      adapterRef: { current: null },
    });
  }
  return sessionRefsMap.get(key)!;
}

// ── 类型 ──────────────────────────────────────────────────────────────────────

export interface AgentSessionState {
  /** store key 对应的 botId，供内部使用 */
  _botId: string;

  bot: BotItem | null;
  isBotUnavailable: boolean;
  currentSession: SessionItem & { id: number };
  conversationList: SessionItem[];

  historyList: AgentMessage[];
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  isLoadingMore: boolean;
  isInitialHistoryLoading: boolean;

  isLoading: boolean;
  qualification: any;

  sessionTools: SessionToolsResult | null;
  selectedModel: string;
  selectedMcpConfigId: number | null;
  thinkingEnabled: boolean;
  selectedFiles: ChatFileDTO[];
  selectedSkillIds: string[];
  videoGenerationEnabled: boolean;
  /** 输入框草稿文本，per-session 缓存，切换会话时保留，发送后清空 */
  draftText: string;
  pendingToolSelection: AgentSession['pendingToolSelection'];
}

export const DEFAULT_SESSION_STATE: Omit<AgentSessionState, '_botId'> = {
  bot: null,
  isBotUnavailable: false,
  currentSession: { id: 0 } as any,
  conversationList: [],
  historyList: [],
  hasMoreOlder: false,
  hasMoreNewer: false,
  isLoadingMore: false,
  isInitialHistoryLoading: false,
  isLoading: false,
  qualification: {},
  sessionTools: null,
  selectedModel: '',
  selectedMcpConfigId: null,
  thinkingEnabled: false,
  selectedFiles: [],
  selectedSkillIds: [],
  videoGenerationEnabled: false,
  draftText: '',
  pendingToolSelection: null,
};

interface AgentSessionStore {
  sessions: Record<string, AgentSessionState>;

  // ── 内部辅助 ──────────────────────────────────────────────────────────────
  _patch: (key: string, patch: Partial<AgentSessionState>) => void;
  _patchHistory: (
    key: string,
    updater: AgentMessage[] | ((prev: AgentMessage[]) => AgentMessage[])
  ) => void;
  _finishCurrentAssistantMessage: (key: string) => void;
  _abortControllers: (key: string) => void;
  _loadTools: (key: string) => Promise<void>;
  _loadConversationHistory: (
    key: string,
    params?: { firstId?: number; lastId?: number }
  ) => Promise<{ list: any[]; hasMore: boolean }>;

  // ── 生命周期 ──────────────────────────────────────────────────────────────
  initSession: (
    key: string,
    botId: string,
    adapter: SessionAdapter,
    options?: { initialDraftText?: string }
  ) => Promise<void>;
  removeSession: (key: string) => void;

  // ── 业务操作 ──────────────────────────────────────────────────────────────
  sendMessage: (
    key: string,
    value: string,
    skillIds?: string[],
    targetBotId?: string
  ) => Promise<void>;
  stopStreaming: (key: string) => Promise<void>;
  abortAll: (key: string) => void;
  loadHistory: (
    key: string,
    anchorId?: number,
    onDone?: () => void,
    anchorItem?: unknown
  ) => Promise<void>;
  loadMoreOlder: (
    key: string,
    onDone?: (prevScrollHeight: number) => void
  ) => Promise<void>;
  loadMoreNewer: (key: string, onDone?: () => void) => Promise<void>;
  switchSession: (key: string, session: SessionItem) => Promise<void>;
  createNewSession: (key: string) => void;
  refreshConversationList: (key: string) => Promise<void>;

  // ── 工具选择 setters ───────────────────────────────────────────────────────
  setSelectedModel: (
    key: string,
    model: string,
    options?: { preserveFiles?: boolean }
  ) => void;
  setSelectedMcpConfigId: (key: string, id: number | null) => void;
  setThinkingEnabled: (key: string, enabled: boolean) => void;
  setSelectedFiles: (
    key: string,
    files: ChatFileDTO[] | ((prev: ChatFileDTO[]) => ChatFileDTO[])
  ) => void;
  clearSelectedFiles: (key: string) => void;
  setSelectedSkillIds: (key: string, ids: string[]) => void;
  setVideoGenerationEnabled: (key: string, enabled: boolean) => void;
  /** 设置输入框草稿文本（per-session 缓存） */
  setDraftText: (key: string, text: string) => void;
  setPendingToolSelection: (
    key: string,
    sel: AgentSession['pendingToolSelection']
  ) => void;

  // ── 消息操作 ──────────────────────────────────────────────────────────────
  setHistoryList: (
    key: string,
    updater: AgentMessage[] | ((prev: AgentMessage[]) => AgentMessage[])
  ) => void;
  addMessages: (key: string, messages: AgentMessage[]) => void;
  updateTimedTaskMessage: (
    key: string,
    taskId: number,
    data: TimedTaskEditData
  ) => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useAgentSessionStore = create<AgentSessionStore>()((set, get) => ({
  sessions: {},

  // ── 内部辅助 ──────────────────────────────────────────────────────────────

  _patch(key, patch) {
    set(state => {
      const s = state.sessions[key];
      if (!s) return state;
      return { sessions: { ...state.sessions, [key]: { ...s, ...patch } } };
    });
  },

  _patchHistory(key, updater) {
    set(state => {
      const s = state.sessions[key];
      if (!s) return state;
      const next =
        typeof updater === 'function' ? updater(s.historyList) : updater;
      return {
        sessions: { ...state.sessions, [key]: { ...s, historyList: next } },
      };
    });
  },

  _finishCurrentAssistantMessage(key) {
    set(state => {
      const s = state.sessions[key];
      if (!s || !s.historyList.length) return state;
      const prev = s.historyList;
      const last = prev[prev.length - 1];
      if (!last?.isLoading) return state;
      const next = [...prev];
      next[next.length - 1] = {
        ...last,
        isLoading: false,
        isThinkingStreaming: false,
        isToolStatusStreaming: false,
        _isFresh: true,
      };
      return {
        sessions: { ...state.sessions, [key]: { ...s, historyList: next } },
      };
    });
  },

  _abortControllers(key) {
    const refs = getOrCreateRefs(key);
    refs.controllersRef.current.forEach(c => c?.abort());
    refs.controllersRef.current = [];
  },

  // ── abortAll ──────────────────────────────────────────────────────────────

  abortAll(key) {
    const refs = getOrCreateRefs(key);
    refs.streamIdRef.current += 1;
    refs.typewriterRef.current?.destroy();
    refs.typewriterRef.current = null;
    refs.thinkingTypewriterRef.current?.destroy();
    refs.thinkingTypewriterRef.current = null;
    get()._abortControllers(key);
    get()._patch(key, { isLoading: false });
  },

  // ── removeSession ─────────────────────────────────────────────────────────

  removeSession(key) {
    set(state => {
      const { [key]: _, ...rest } = state.sessions;
      return { sessions: rest };
    });
  },

  // ── initSession ──────────────────────────────────────────────────────────

  async initSession(key, botId, adapter, options?) {
    const existing = get().sessions[key];
    // initSession 重置会话相关状态，但保留以下跨初始化存活的字段：
    // - pendingToolSelection：外部预设的工具选项
    // - draftText：输入框草稿，切换会话后回来应恢复；若调用方传入 initialDraftText
    //   （来自工作台 pending command），优先使用，确保工作台输入覆盖旧草稿
    // - selectedFiles：已选附件，切换后回来应恢复
    // - 工具选项（selectedModel / selectedMcpConfigId / thinkingEnabled 等）：
    //   若外部已有值则保留，_loadTools 会在工具列表加载完成后按 pendingToolSelection 覆盖
    set(state => ({
      sessions: {
        ...state.sessions,
        [key]: {
          ...DEFAULT_SESSION_STATE,
          _botId: botId,
          // 优先用工作台 pending command 的文字，否则恢复旧草稿
          draftText: options?.initialDraftText ?? existing?.draftText ?? '',
          selectedFiles: existing?.selectedFiles ?? [],
          // 保留工具选项（_loadTools 后若有 pendingToolSelection 会覆盖）
          selectedModel: existing?.selectedModel ?? '',
          selectedMcpConfigId: existing?.selectedMcpConfigId ?? null,
          thinkingEnabled: existing?.thinkingEnabled ?? false,
          videoGenerationEnabled: existing?.videoGenerationEnabled ?? false,
          selectedSkillIds: existing?.selectedSkillIds ?? [],
          pendingToolSelection: existing?.pendingToolSelection ?? null,
        },
      },
    }));
    const refs = getOrCreateRefs(key);
    refs.adapterRef.current = adapter;
    refs.streamIdRef.current += 1;
    refs.controllersRef.current.forEach(c => c?.abort());
    refs.controllersRef.current = [];
    refs.historyLoadIdRef.current += 1;

    try {
      const res: any = await feimaChatBotInfo({ botId });
      if (!res?.id) {
        get()._patch(key, { isBotUnavailable: true });
        return;
      }
      get()._patch(key, { bot: res });
      await get()._loadTools(key);

      // 写入 adapter 提供的业务初始状态（如 note 的 conversationList / currentSession）
      if (adapter.getInitialPatch) {
        const patch = await adapter.getInitialPatch(res);
        get()._patch(key, patch);
      }

      await get().loadHistory(key);
    } catch {
      get()._patch(key, { isBotUnavailable: true });
    }
  },

  // ── 内部：加载工具选项（不对外暴露） ────────────────────────────────────
  // 通过扩展 store 私有方法的方式实现
  async _loadTools(key: string) {
    if (!get().sessions[key]) return;
    try {
      const tools = await feimaChatSessionGetTools();
      // await 结束后重新读最新状态，避免读取 initSession 前的快照
      const pending = get().sessions[key]?.pendingToolSelection;
      if (pending) {
        get()._patch(key, {
          sessionTools: tools ?? null,
          selectedModel: pending.model,
          selectedMcpConfigId: pending.mcpConfigId,
          thinkingEnabled: pending.thinkingEnabled,
          selectedSkillIds: pending.skillIds ?? [],
          pendingToolSelection: null,
        });
      } else {
        const firstModel = tools?.models?.[0]?.modelId ?? '';
        const webSearch = tools?.mcpTools?.find(
          (t: any) => t.name === '联网搜索'
        );
        const mcpId = webSearch ? webSearch.id ?? null : null;
        const current = get().sessions[key];
        // 若 initSession 已保留了上一次的工具选项，且该选项在新工具列表里仍然有效，
        // 则保留用户上次的选择；否则回退到默认值
        const availableModelIds = (tools?.models ?? []).map(
          (m: any) => m.modelId
        );
        const preservedModel =
          current?.selectedModel &&
          availableModelIds.includes(current.selectedModel)
            ? current.selectedModel
            : firstModel;
        const availableMcpIds = (tools?.mcpTools ?? []).map((t: any) => t.id);
        const preservedMcp =
          current?.selectedMcpConfigId != null &&
          availableMcpIds.includes(current.selectedMcpConfigId)
            ? current.selectedMcpConfigId
            : mcpId;
        get()._patch(key, {
          sessionTools: tools ?? null,
          selectedModel: preservedModel,
          selectedMcpConfigId: preservedMcp,
        });
      }
    } catch {
      get()._patch(key, { sessionTools: null });
    }
  },

  // ── 内部：加载会话历史记录底层函数 ─────────────────────────────────────
  // 只注入 size，具体接口由 adapter.historyFetcher 决定
  async _loadConversationHistory(
    key: string,
    params: { firstId?: number; lastId?: number } = {}
  ): Promise<{ list: any[]; hasMore: boolean }> {
    const fetcher = getOrCreateRefs(key).adapterRef.current?.historyFetcher;
    if (!fetcher) return { list: [], hasMore: false };
    return fetcher({ size: HISTORY_PAGE_SIZE, ...params });
  },

  // ── loadHistory ───────────────────────────────────────────────────────────

  async loadHistory(key, anchorId, onDone, anchorItem) {
    const refs = getOrCreateRefs(key);
    const loadId = refs.historyLoadIdRef.current + 1;
    refs.historyLoadIdRef.current = loadId;
    get()._patch(key, { isInitialHistoryLoading: true });

    const s = get().sessions[key];
    if (!s?.bot?.id) {
      get()._patch(key, { isInitialHistoryLoading: false });
      return;
    }
    const currentBot = s.bot;
    const currentUser = getStorageUser();

    try {
      if (anchorId) {
        const [olderRes, newerRes] = await Promise.all([
          get()._loadConversationHistory(key, { lastId: anchorId }),
          get()._loadConversationHistory(key, { firstId: anchorId }),
        ]);
        if (refs.historyLoadIdRef.current !== loadId) return;
        const olderList = olderRes.list;
        const newerFiltered = filterAnchorMessage(newerRes.list, anchorId);
        const hasAnchor = [...olderList, ...newerFiltered].some(
          item => getConversationId(item) === anchorId
        );
        const anchorMessageItem = hasAnchor
          ? null
          : getAnchorMessageItem(anchorItem, anchorId);
        const merged = processConversationList(
          [
            ...olderList,
            ...(anchorMessageItem ? [anchorMessageItem] : []),
            ...newerFiltered,
          ],
          currentBot,
          currentUser
        );
        get()._patch(key, {
          historyList: mergeDedup([], merged),
          hasMoreOlder: olderRes.hasMore,
          hasMoreNewer: newerRes.hasMore,
          isLoadingMore: false,
        });
      } else {
        const res = await get()._loadConversationHistory(key);
        if (refs.historyLoadIdRef.current !== loadId) return;
        const list = res.list;
        const processed = processConversationList(
          list,
          currentBot,
          currentUser
        );
        const currentSession = get().sessions[key]?.currentSession;
        get()._patch(key, {
          historyList: processed.length
            ? processed
            : [createWelcomeMessage(currentBot, currentSession?.id)],
          hasMoreOlder: res.hasMore,
          hasMoreNewer: false,
          isLoadingMore: false,
        });
      }

      if (onDone) {
        onDone();
      } else {
        requestAnimationFrame(() => {
          const el = document.querySelector(
            '.chat-scroll-area'
          ) as HTMLElement | null;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    } catch {
      // 加载失败保留当前内容
    } finally {
      if (refs.historyLoadIdRef.current === loadId) {
        get()._patch(key, { isInitialHistoryLoading: false });
      }
    }
  },

  // ── loadMoreOlder ─────────────────────────────────────────────────────────

  async loadMoreOlder(key, onDone) {
    const s = get().sessions[key];
    if (!s || s.isLoadingMore || !s.hasMoreOlder) return;
    const topRawId = s.historyList.find(item => item._rawId)?._rawId;
    if (!topRawId) return;
    get()._patch(key, { isLoadingMore: true });
    const prevScrollHeight =
      document.querySelector('.chat-scroll-area')?.scrollHeight ?? 0;
    try {
      const res = await get()._loadConversationHistory(key, {
        lastId: topRawId,
      });
      const currentBot = get().sessions[key]?.bot;
      const prepended = processConversationList(
        res.list,
        currentBot,
        getStorageUser()
      );
      get()._patchHistory(key, current => mergeDedup(prepended, current));
      get()._patch(key, { hasMoreOlder: res.hasMore, isLoadingMore: false });
      onDone?.(prevScrollHeight);
    } catch {
      get()._patch(key, { isLoadingMore: false });
    }
  },

  // ── loadMoreNewer ─────────────────────────────────────────────────────────

  async loadMoreNewer(key, onDone) {
    const s = get().sessions[key];
    if (!s || s.isLoadingMore || !s.hasMoreNewer) return;
    const bottomRawId = [...s.historyList]
      .reverse()
      .find(item => item._rawId)?._rawId;
    if (!bottomRawId) return;
    get()._patch(key, { isLoadingMore: true });
    try {
      const res = await get()._loadConversationHistory(key, {
        firstId: bottomRawId,
      });
      const currentBot = get().sessions[key]?.bot;
      const appended = processConversationList(
        res.list,
        currentBot,
        getStorageUser()
      );
      get()._patchHistory(key, current => mergeDedup(current, appended));
      get()._patch(key, { hasMoreNewer: res.hasMore, isLoadingMore: false });
      onDone?.();
    } catch {
      get()._patch(key, { isLoadingMore: false });
    }
  },

  // ── refreshConversationList ───────────────────────────────────────────────

  async refreshConversationList(key) {
    const s = get().sessions[key];
    if (!s?.bot?.id) return;
    // 有 getInitialPatch 的 adapter 自管 session 列表，不需要此处刷新
    const adapter = getOrCreateRefs(key).adapterRef.current;
    if (adapter?.getInitialPatch) return;
    const { feimaChatSessionList: listApi } = await import('@/api/sessionApi');
    const res = await listApi({ botId: s.bot.id });
    get()._patch(key, { conversationList: res?.list ?? [] });
  },

  // ── switchSession ─────────────────────────────────────────────────────────

  async switchSession(key, session) {
    get().abortAll(key);
    get()._patch(key, {
      currentSession: session as any,
      historyList: [],
      hasMoreOlder: false,
      hasMoreNewer: false,
    });
    if ((session as any).id) {
      await get().loadHistory(key);
    }
  },

  // ── createNewSession ──────────────────────────────────────────────────────

  createNewSession(key) {
    get().abortAll(key);
    const refs = getOrCreateRefs(key);
    refs.historyLoadIdRef.current += 1;
    const bot = get().sessions[key]?.bot;
    get()._patch(key, {
      currentSession: { id: 0 } as any,
      historyList: [createWelcomeMessage(bot)],
      hasMoreOlder: false,
      hasMoreNewer: false,
      isInitialHistoryLoading: false,
    });
  },

  // ── stopStreaming ─────────────────────────────────────────────────────────

  async stopStreaming(key) {
    const sessionId = get().sessions[key]?.currentSession?.id;
    const refs = getOrCreateRefs(key);
    const activeStreamId = refs.streamIdRef.current;
    try {
      if (sessionId) {
        await feimaChatSessionConversationStop(
          { sessionId },
          { noMessage: false }
        );
      }
    } catch {
      // 接口繁忙也要释放前端流式状态
    } finally {
      if (refs.streamIdRef.current === activeStreamId) {
        refs.streamIdRef.current += 1;
      }
      refs.typewriterRef.current?.destroy();
      refs.typewriterRef.current = null;
      refs.thinkingTypewriterRef.current?.destroy();
      refs.thinkingTypewriterRef.current = null;
      get()._abortControllers(key);
      get()._finishCurrentAssistantMessage(key);
      get()._patch(key, { isLoading: false });
    }
  },

  // ── sendMessage（SSE）────────────────────────────────────────────────────

  async sendMessage(key, value, skillIds = [], targetBotId) {
    const s = get().sessions[key];
    if (!s) return;

    const filesSnapshot = [...s.selectedFiles];
    const skillIdsSnapshot =
      skillIds.length > 0 ? skillIds : [...s.selectedSkillIds];
    const currentBot = s.bot;
    const userInfo = getStorageUser();
    const botIdSnapshot = (currentBot as any)?.id as string | undefined;
    if (!botIdSnapshot) return;

    const refs = getOrCreateRefs(key);
    const streamId = ++refs.streamIdRef.current;

    const isBotStale = () => get().sessions[key]?.bot?.id !== botIdSnapshot;
    const isStreamStale = () =>
      refs.streamIdRef.current !== streamId || isBotStale();

    // 无会话时先通过 adapter 创建
    let sessionId = get().sessions[key]?.currentSession?.id ?? 0;
    if (!sessionId) {
      sessionId =
        (await refs.adapterRef.current?.ensureSession(value, patch =>
          get()._patch(key, patch)
        )) ?? 0;
      if (isStreamStale()) return;
      if (!sessionId) return;
    }
    notifyAgentChatPinned(botIdSnapshot, currentBot);

    // 乐观追加用户消息 + 助手占位
    const userMsg: AgentMessage = {
      id: Math.random(),
      sessionId,
      conversation: value.split('_^_')[0],
      roleType: 'user',
      nickname: '用户',
      avatar: (userInfo as any)?.avatar || (userInfo as any)?.headPic || '',
      createTime: Date.now(),
      ...(filesSnapshot.length > 0
        ? { conversationExtJsonDTO: { files: filesSnapshot } }
        : {}),
      ...(get().sessions[key]?.videoGenerationEnabled
        ? { skipMemoryNotification: true }
        : {}),
    };
    const assistantMsg: AgentMessage = {
      id: Math.random(),
      sessionId,
      conversation: '',
      roleType: 'assistant',
      nickname: '智能体',
      avatar: (currentBot as any)?.avatar || '',
      createTime: Date.now(),
      isLoading: true,
      ...(get().sessions[key]?.videoGenerationEnabled
        ? {
            skipMemoryNotification: true,
            // 视频模式：SSE 期间提前写入 PENDING 状态，使消息路由到 VideoTaskMessage
            // 而非普通三点动画；服务端返回真实状态后会被 mergeDedup 覆盖
            conversationExtJsonDTO: {
              videoTaskExt: { videoTaskStatus: 'PENDING' },
            },
          }
        : {}),
    };
    get()._patchHistory(key, prev => [...prev, userMsg, assistantMsg]);
    get()._patch(key, { isLoading: true, selectedFiles: [] });

    // 创建打字机队列：SSE 字符经队列按速率逐字写入 store
    const msgId = assistantMsg.id;
    const userMsgId = userMsg.id;
    let contentTypewriterDone = false;
    let thinkingTypewriterDone = false;
    let thinkingTypewriterFlushed = false;
    // 补拉回来的服务端真实 id，打字机 onDone 时一并写入，使后续 mergeDedup 能按 id 正确去重
    const serverRealIdRef = { current: 0 };
    const finishCurrentMessageWhenQueuesDone = () => {
      if (!contentTypewriterDone || !thinkingTypewriterDone) return;
      get()._patchHistory(key, prev => {
        const next = [...prev];
        const idx = next.findIndex(m => m.id === msgId);
        if (idx === -1) return prev;
        const realId = serverRealIdRef.current;
        next[idx] = {
          ...next[idx],
          // 若补拉已返回，同步将 id 替换为服务端真实 id，保证后续 mergeDedup 去重准确
          ...(realId ? { id: realId, _rawId: realId } : {}),
          isLoading: false,
          _isFresh: true,
        };
        return next;
      });
      get()._patch(key, { isLoading: false });
    };
    const flushThinkingTypewriter = () => {
      if (thinkingTypewriterFlushed) return;
      thinkingTypewriterFlushed = true;
      refs.thinkingTypewriterRef.current?.flush();
    };
    const drainThinkingTypewriter = () => {
      if (thinkingTypewriterFlushed) return;
      thinkingTypewriterFlushed = true;
      refs.thinkingTypewriterRef.current?.flush();
      refs.thinkingTypewriterRef.current?.drain();
    };
    refs.typewriterRef.current?.destroy();
    refs.thinkingTypewriterRef.current?.destroy();
    refs.typewriterRef.current = new TypewriterQueue({
      rate: 45,
      onOutput: ch => {
        if (isStreamStale()) return;
        get()._patchHistory(key, prev => {
          const next = [...prev];
          const idx = next.findIndex(m => m.id === msgId);
          if (idx === -1) return prev;
          next[idx] = {
            ...next[idx],
            conversation: next[idx].conversation + ch,
          };
          return next;
        });
      },
      onDone: () => {
        if (isStreamStale()) return;
        contentTypewriterDone = true;
        refs.typewriterRef.current = null;
        finishCurrentMessageWhenQueuesDone();
      },
    });
    refs.thinkingTypewriterRef.current = new TypewriterQueue({
      rate: 200,
      onOutput: ch => {
        if (isStreamStale()) return;
        get()._patchHistory(key, prev => {
          const next = [...prev];
          const idx = next.findIndex(m => m.id === msgId);
          if (idx === -1) return prev;
          next[idx] = {
            ...next[idx],
            thinkingContent: (next[idx].thinkingContent || '') + ch,
          };
          return next;
        });
      },
      onDone: () => {
        if (isStreamStale()) return;
        thinkingTypewriterDone = true;
        refs.thinkingTypewriterRef.current = null;
        get()._patchHistory(key, prev => {
          const next = [...prev];
          const idx = next.findIndex(m => m.id === msgId);
          if (idx === -1) return prev;
          next[idx] = { ...next[idx], isThinkingStreaming: false };
          return next;
        });
        finishCurrentMessageWhenQueuesDone();
      },
    });

    // SSE
    const currentS2 = get().sessions[key];
    const fetchRequest = await fetchSourceApi('/session/conversation/send', {
      conversation: value.split('_^_')[0],
      sessionId,
      ...(targetBotId ? { botId: targetBotId } : {}),
      ...(currentS2?.selectedModel ? { model: currentS2.selectedModel } : {}),
      ...(currentS2?.selectedMcpConfigId != null
        ? { selectedMcpConfigId: currentS2.selectedMcpConfigId }
        : {}),
      ...(currentS2?.thinkingEnabled ? { thinkingEnabled: true } : {}),
      ...(filesSnapshot.length > 0 ? { files: filesSnapshot } : {}),
      ...(currentS2?.videoGenerationEnabled ? { type: 'video_task' } : {}),
      ...(refs.adapterRef.current?.getExtraSendParams?.() ?? {}),
      skillIds: skillIdsSnapshot,
      onMessage: (data: any) => {
        if (isStreamStale()) return;

        // 非正文内容直接写状态；thinking 文本本身仍走专用打字机
        if (
          data.type === 'thinking' ||
          data.type === 'tool_status' ||
          data.type === 'memory_extract'
        ) {
          get()._patchHistory(key, prev => {
            const next = [...prev];
            const last = { ...next[next.length - 1] };

            if (data.type !== 'tool_status' && last.toolStatusList?.length)
              last.isToolStatusStreaming = false;

            if (data.type === 'thinking') {
              if (thinkingTypewriterFlushed) {
                last.thinkingContent =
                  (last.thinkingContent || '') + (data.content || '');
              } else {
                last.isThinkingStreaming = true;
              }
            } else if (data.type === 'tool_status') {
              if (data.content)
                last.toolStatusList = [
                  ...(last.toolStatusList || []),
                  data.content,
                ];
              last.isToolStatusStreaming = true;
            } else if (data.type === 'memory_extract') {
              const newItem = {
                id: data.memoryId,
                content: data.content,
                readStatus: 'UNREAD' as const,
              };
              const existing = Array.isArray(last.memoryList)
                ? last.memoryList
                : [];
              if (!existing.some((m: any) => m.id === data.memoryId)) {
                last.memoryList = [...existing, newItem];
              }
            }

            next[next.length - 1] = last;
            return next;
          });
        }

        // 文本内容推入打字机队列，由打字机按速率写 store
        if (
          data.content &&
          data.type !== 'thinking' &&
          data.type !== 'tool_status'
        ) {
          // 有正文时认为思考阶段结束，立即排空思考队列，避免两个区域并行打字
          const lastMsg = get().sessions[key]?.historyList.at(-1);
          if (lastMsg?.isThinkingStreaming || lastMsg?.isToolStatusStreaming) {
            get()._patchHistory(key, prev => {
              const next = [...prev];
              next[next.length - 1] = {
                ...next[next.length - 1],
                isThinkingStreaming: false,
                isToolStatusStreaming: false,
              };
              return next;
            });
          }
          drainThinkingTypewriter();
          refs.typewriterRef.current?.enqueue(data.content);
        }

        if (
          data.content &&
          data.type === 'thinking' &&
          !thinkingTypewriterFlushed
        ) {
          refs.thinkingTypewriterRef.current?.enqueue(data.content);
        }

        // SSE 推送完毕：通知打字机进入排空模式
        if (data.done) {
          get()._patchHistory(key, prev => {
            const next = [...prev];
            next[next.length - 1] = {
              ...next[next.length - 1],
              isToolStatusStreaming: false,
            };
            return next;
          });
          flushThinkingTypewriter();
          refs.typewriterRef.current?.flush();

          // 拉取服务端真实 id，更新消息 id / _rawId 及元数据
          refs.adapterRef.current
            ?.historyFetcher({ size: MERGED_LIST_FETCH_SIZE })
            .then(({ list }) => {
              if (isStreamStale()) return;
              if (!list.length) return;
              const currentBot2 = get().sessions[key]?.bot;
              const processed = processConversationList(
                list,
                currentBot2,
                getStorageUser()
              );
              const serverAssistantMsg = processed[processed.length - 1];
              if (
                !serverAssistantMsg ||
                serverAssistantMsg.roleType !== 'assistant'
              )
                return;
              const realAssistantId =
                serverAssistantMsg._rawId ?? serverAssistantMsg.id;
              if (!realAssistantId) return;

              // 取倒数第二条作为对应的用户消息
              const serverUserMsg = processed[processed.length - 2];
              const realUserId =
                serverUserMsg?.roleType === 'user'
                  ? serverUserMsg._rawId ?? serverUserMsg.id
                  : 0;

              // 记录助手消息真实 id，供打字机 onDone 回调读取（打字机比补拉慢时走此路径）
              serverRealIdRef.current = realAssistantId;

              get()._patchHistory(key, prev => {
                const next = [...prev];

                // 替换用户消息 id：userMsg 无打字机依赖，补拉回来时直接通过临时 id 定位
                if (realUserId) {
                  const userIdx = next.findIndex(m => m.id === userMsgId);
                  if (userIdx !== -1 && !next[userIdx]._rawId) {
                    next[userIdx] = {
                      ...next[userIdx],
                      id: realUserId,
                      _rawId: realUserId,
                    };
                  }
                }

                // 替换助手消息 id：打字机已完成（isLoading = false）时补做；
                // 若打字机还在运行则跳过，等 finishCurrentMessageWhenQueuesDone 读取 serverRealIdRef 更新
                const last = next[next.length - 1];
                if (!last?.isLoading && !last?._rawId) {
                  next[next.length - 1] = {
                    ...last,
                    id: realAssistantId,
                    _rawId: realAssistantId,
                    questionList:
                      last.questionList ?? serverAssistantMsg.questionList,
                  };
                }

                return next;
              });
            })
            .catch(() => {});

          // 面具模式：拉取预测问题
          const maskType = get().sessions[key]?.currentSession?.maskType;
          if (maskType === 2) {
            feimaChatSessionGetAnticipateQuestion({
              conversationId: data.conversationId,
              botId: (get().sessions[key]?.bot as any)?.id,
            })
              .then(res => {
                if (isStreamStale()) return;
                get()._patchHistory(key, prev => {
                  const next = [...prev];
                  next[next.length - 1] = {
                    ...next[next.length - 1],
                    questionList: res.list,
                  };
                  return next;
                });
              })
              .catch(() => {});
          }
        }
      },
      onError: (_err: any) => {
        if (isStreamStale()) return;
        refs.typewriterRef.current?.destroy();
        refs.typewriterRef.current = null;
        refs.thinkingTypewriterRef.current?.destroy();
        refs.thinkingTypewriterRef.current = null;
        get()._patchHistory(key, prev => {
          const next = [...prev];
          next[next.length - 1] = {
            ...next[next.length - 1],
            isLoading: false,
            isThinkingStreaming: false,
            isToolStatusStreaming: false,
            isError: true,
            conversation: '请求失败，请稍后重试',
          };
          return next;
        });
        get()._patch(key, { isLoading: false });
      },
    });

    if (isStreamStale()) {
      fetchRequest.controller.abort();
      return;
    }
    refs.controllersRef.current.push(fetchRequest.controller);
    try {
      await fetchRequest();
    } catch {
      if (isStreamStale()) return;
      // SSE 连接异常：销毁打字机，直接标记错误
      refs.typewriterRef.current?.destroy();
      refs.typewriterRef.current = null;
      refs.thinkingTypewriterRef.current?.destroy();
      refs.thinkingTypewriterRef.current = null;
      get()._patchHistory(key, prev => {
        const next = [...prev];
        next[next.length - 1] = {
          ...next[next.length - 1],
          isLoading: false,
          isThinkingStreaming: false,
          isToolStatusStreaming: false,
          isError: true,
          conversation: '请求失败，请稍后重试',
        };
        return next;
      });
      get()._patch(key, { isLoading: false });
      return;
    } finally {
      refs.controllersRef.current = refs.controllersRef.current.filter(
        c => c !== fetchRequest.controller
      );
    }
    // 正常结束后 isLoading 由 TypewriterQueue.onDone 回调统一关闭，此处不设
  },

  // ── 工具选择 setters ──────────────────────────────────────────────────────

  setSelectedModel(key, model, options) {
    const s = get().sessions[key];
    if (!s) return;
    const tools = s.sessionTools;
    const targetModel = tools?.models?.find((m: any) => m.modelId === model);
    const nextThinking = targetModel?.supportsThinking
      ? s.thinkingEnabled
      : false;
    get()._patch(key, {
      selectedModel: model,
      thinkingEnabled: nextThinking,
      ...(options?.preserveFiles ? {} : { selectedFiles: [] }),
    });
  },

  setSelectedMcpConfigId(key, id) {
    get()._patch(key, { selectedMcpConfigId: id });
  },

  setThinkingEnabled(key, enabled) {
    get()._patch(key, { thinkingEnabled: enabled });
  },

  setSelectedFiles(key, files) {
    const s = get().sessions[key];
    if (!s) return;
    const next = typeof files === 'function' ? files(s.selectedFiles) : files;
    get()._patch(key, { selectedFiles: next });
  },

  clearSelectedFiles(key) {
    get()._patch(key, { selectedFiles: [] });
  },

  setSelectedSkillIds(key, ids) {
    get()._patch(key, { selectedSkillIds: ids });
  },

  setVideoGenerationEnabled(key, enabled) {
    get()._patch(key, { videoGenerationEnabled: enabled });
  },

  setDraftText(key, text) {
    get()._patch(key, { draftText: text });
  },

  setPendingToolSelection(key, sel) {
    get()._patch(key, { pendingToolSelection: sel });
  },

  // ── 消息操作 ──────────────────────────────────────────────────────────────

  setHistoryList(key, updater) {
    get()._patchHistory(key, updater);
  },

  addMessages(key, messages) {
    get()._patchHistory(key, prev => mergeDedup(prev, messages));
  },

  updateTimedTaskMessage(key, taskId, data) {
    const isTarget = (v?: number | string) => String(v) === String(taskId);
    const fanNames = data.sessionResults
      .map((r: any) => r.title)
      .filter(Boolean);
    const groupName = fanNames.length > 0 ? fanNames.join('、') : '关注列表';
    get()._patchHistory(key, prev =>
      prev.map(item => {
        if (
          item.messageType === 'timedTask' &&
          isTarget(item.timedTaskData?.taskId)
        ) {
          const taskInfoExt = item.conversationExtJsonDTO?.taskInfoExt;
          const newsTaskExt = taskInfoExt?.newsTaskExt || {};
          const nextTaskType = data.taskType ?? item.timedTaskData.taskType;
          const nextTaskStatus =
            data.draft === undefined
              ? taskInfoExt?.taskStatus
              : data.draft
              ? TIMED_TASK_STATUS.DRAFT
              : TIMED_TASK_STATUS.PENDING;
          return {
            ...item,
            conversation: data.content,
            conversationExtJsonDTO: item.conversationExtJsonDTO
              ? {
                  ...item.conversationExtJsonDTO,
                  taskInfoExt: taskInfoExt
                    ? {
                        ...taskInfoExt,
                        executeTime: data.scheduledTime,
                        receiveTotal: data.sessionCount,
                        title: data.title,
                        tags: data.tags,
                        annotation: data.annotation,
                        taskType: nextTaskType,
                        taskStatus: nextTaskStatus,
                        fetchCount: data.fetchCount,
                        timeRange: data.timeRange,
                        sendSessionIds: data.sendSessionIds,
                        sessionResults: data.sessionResults,
                        sourceIds: data.sourceIds,
                        aiModel: data.aiModel,
                        aiStrategy: data.aiStrategy,
                        newsTaskExt: {
                          ...newsTaskExt,
                          taskType: nextTaskType,
                          keywords: data.content,
                          fetchCount: data.fetchCount,
                          timeRange: data.timeRange,
                          sourceIds: data.sourceIds,
                          sendToSelected:
                            data.sendToFeatured === undefined
                              ? newsTaskExt.sendToSelected
                              : data.sendToFeatured
                              ? 1
                              : 0,
                          deduplication:
                            data.dedup === undefined
                              ? newsTaskExt.deduplication
                              : data.dedup
                              ? 1
                              : 0,
                          aiModel: data.aiModel,
                          aiStrategy: data.aiStrategy,
                          syncToSocial: data.syncToSocial,
                          socialPublishMode: data.socialPublishMode,
                          socialPublishTargets: data.socialPublishTargets,
                        },
                      }
                    : taskInfoExt,
                }
              : item.conversationExtJsonDTO,
            timedTaskData: {
              ...item.timedTaskData,
              scheduledTime: data.scheduledTime,
              content: data.content,
              sessionCount: data.sessionCount,
              groupCount: data.sessionCount,
              title: data.title,
              tags: data.tags,
              annotation: data.annotation,
              taskType: nextTaskType,
              fetchCount: data.fetchCount,
              timeRange: data.timeRange,
              dedup: data.dedup,
              sendToFeatured: data.sendToFeatured,
              sourceIds: data.sourceIds,
              aiModel: data.aiModel,
              aiStrategy: data.aiStrategy,
              groupName,
              sendSessionIds: data.sendSessionIds,
              sessionResults: data.sessionResults,
              draft: data.draft,
              syncToSocial: data.syncToSocial,
              socialPublishMode: data.socialPublishMode,
              socialPublishTargets: data.socialPublishTargets,
              t2vModelId: data.t2vModelId,
            },
          };
        }
        if (
          item.messageType === 'timedTaskResult' &&
          isTarget(item.timedTaskResult?.taskId)
        ) {
          return {
            ...item,
            conversation: '',
            isLoading: false,
            timedTaskResult: {
              ...item.timedTaskResult,
              scheduledTime: data.scheduledTime,
              sessionCount: data.sessionCount,
              taskStatus:
                data.draft === undefined
                  ? item.timedTaskResult.taskStatus
                  : data.draft
                  ? TIMED_TASK_STATUS.DRAFT
                  : TIMED_TASK_STATUS.PENDING,
              taskStatusText:
                data.draft === undefined
                  ? item.timedTaskResult.taskStatusText
                  : getTimedTaskStatusText(
                      data.draft
                        ? TIMED_TASK_STATUS.DRAFT
                        : TIMED_TASK_STATUS.PENDING
                    ),
              recallFlag:
                data.draft === undefined
                  ? item.timedTaskResult.recallFlag
                  : !data.draft,
            },
          };
        }
        return item;
      })
    );
  },
}));
